import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Volume2, VolumeX, ChevronDown, Zap, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

// ============================================
// FONT LOADER
// ============================================
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');

    .font-display {
      font-family: 'Space Grotesk', 'Inter', -apple-system, sans-serif;
    }
    .font-mono {
      font-family: 'JetBrains Mono', 'Fira Code', monospace;
    }

    /* Glowing text effect */
    .glow-text {
      text-shadow: 0 0 20px currentColor, 0 0 40px currentColor;
    }

    /* Scanline effect */
    .scanlines::after {
      content: '';
      position: absolute;
      inset: 0;
      background: repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0, 0, 0, 0.1) 2px,
        rgba(0, 0, 0, 0.1) 4px
      );
      pointer-events: none;
    }

    /* CRT glow */
    .crt-glow {
      box-shadow:
        inset 0 0 60px rgba(34, 211, 238, 0.05),
        0 0 100px rgba(34, 211, 238, 0.1);
    }

    /* Pulse animation */
    @keyframes pulse-glow {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 1; }
    }
    .pulse-glow {
      animation: pulse-glow 2s ease-in-out infinite;
    }

    /* Typewriter cursor */
    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0; }
    }
    .cursor-blink {
      animation: blink 1s step-end infinite;
    }
  `}</style>
);

// ============================================
// AMBIENT SOUND ENGINE
// ============================================
class AmbientSoundEngine {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private oscillators: OscillatorNode[] = [];
  private enabled: boolean = true;
  private currentDepth: number = 0;

  private getContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0.03; // Very subtle
      this.masterGain.connect(this.audioContext.destination);
    }
    return this.audioContext;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (this.masterGain) {
      this.masterGain.gain.value = enabled ? 0.03 : 0;
    }
  }

  isEnabled() {
    return this.enabled;
  }

  // Update ambient based on depth (0-12)
  updateDepth(depth: number) {
    if (!this.enabled || depth === this.currentDepth) return;
    this.currentDepth = depth;

    try {
      const ctx = this.getContext();

      // Stop existing oscillators
      this.oscillators.forEach(osc => {
        try { osc.stop(); } catch (e) {}
      });
      this.oscillators = [];

      if (depth === 0 || !this.masterGain) return;

      // Create layered drone based on depth
      // Higher = bright, digital | Lower = dark, analog
      const baseFreq = 80 - (depth * 4); // Gets lower as we go deeper
      const harmonics = [1, 1.5, 2, 2.5, 3];

      harmonics.forEach((mult, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = depth > 8 ? 'sawtooth' : depth > 4 ? 'triangle' : 'sine';
        osc.frequency.value = baseFreq * mult;
        gain.gain.value = 0.02 / (i + 1); // Decreasing harmonics

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start();

        this.oscillators.push(osc);
      });

      // Add noise for hardware/electron layers
      if (depth >= 10) {
        const bufferSize = ctx.sampleRate * 2;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          data[i] = (Math.random() * 2 - 1) * 0.3;
        }

        const noise = ctx.createBufferSource();
        const noiseGain = ctx.createGain();
        const noiseFilter = ctx.createBiquadFilter();

        noise.buffer = buffer;
        noise.loop = true;
        noiseFilter.type = 'lowpass';
        noiseFilter.frequency.value = 200 + (depth - 10) * 100;
        noiseGain.gain.value = 0.01;

        noise.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(this.masterGain!);
        noise.start();
      }
    } catch (e) {}
  }

  // Play transition sound when crossing boundaries
  playTransition(type: 'kernel' | 'hardware' | 'electron') {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      const now = ctx.currentTime;

      if (type === 'kernel') {
        // Warning siren for kernel boundary
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(600, now + 0.2);
        osc.frequency.linearRampToValueAtTime(400, now + 0.4);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

        osc.start(now);
        osc.stop(now + 0.5);
      } else if (type === 'electron') {
        // Electric crackle
        [800, 1200, 1600, 2000].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, now + i * 0.05);
          gain.gain.linearRampToValueAtTime(0.05, now + i * 0.05 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.1);

          osc.start(now + i * 0.05);
          osc.stop(now + i * 0.05 + 0.1);
        });
      }
    } catch (e) {}
  }

  stop() {
    this.oscillators.forEach(osc => {
      try { osc.stop(); } catch (e) {}
    });
    this.oscillators = [];
  }
}

const soundEngine = new AmbientSoundEngine();

// ============================================
// ELECTRON PARTICLE SYSTEM
// ============================================
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  hue: number;
}

const ElectronParticles: React.FC<{ active: boolean }> = ({ active }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    particlesRef.current = Array.from({ length: 150 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 3,
      vy: (Math.random() - 0.5) * 3,
      size: Math.random() * 3 + 1,
      alpha: Math.random() * 0.8 + 0.2,
      hue: 180 + Math.random() * 40, // Cyan range
    }));

    const animate = () => {
      ctx.fillStyle = 'rgba(5, 5, 15, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach((p, i) => {
        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Bounce off edges
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, 100%, 70%, ${p.alpha})`;
        ctx.fill();

        // Draw glow
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4);
        gradient.addColorStop(0, `hsla(${p.hue}, 100%, 70%, ${p.alpha * 0.3})`);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fillRect(p.x - p.size * 4, p.y - p.size * 4, p.size * 8, p.size * 8);

        // Draw connections
        particlesRef.current.slice(i + 1).forEach(p2 => {
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `hsla(${p.hue}, 100%, 70%, ${(1 - dist / 100) * 0.3})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.8 }}
    />
  );
};

// ============================================
// LAYER DATA
// ============================================
const layers = [
  {
    id: 1,
    name: 'Your Code',
    zone: 'user',
    color: '#f7df1e',
    colorName: 'JavaScript Yellow',
    time: '0ns',
    lines: 1,
    depth: '1x',
    code: `console.log("Hello");`,
    insight: 'You write 27 characters. Behind them: 50,000 lines of code waiting to execute.',
  },
  {
    id: 2,
    name: 'V8 Parser',
    zone: 'v8',
    color: '#4ade80',
    colorName: 'V8 Green',
    time: '~0.5ms',
    lines: 8000,
    depth: '10x',
    code: `// Tokenize → AST
{
  type: "CallExpression",
  callee: {
    type: "MemberExpression",
    object: "console",
    property: "log"
  },
  arguments: [{ type: "Literal", value: "Hello" }]
}`,
    insight: 'Your string becomes tokens. Tokens become a tree. The tree becomes bytecode.',
  },
  {
    id: 3,
    name: 'Ignition',
    zone: 'v8',
    color: '#22d3ee',
    colorName: 'Ignition Cyan',
    time: '~0.1ms',
    lines: 12000,
    depth: '100x',
    code: `// V8 Bytecode
LdaGlobal [console]     // Load console
Star r0                 // Store in register
LdaConstant ["Hello"]   // Load string
Star r1                 // Store in register
CallProperty1 r0, [log], r1  // Call method`,
    insight: 'The interpreter runs bytecode step by step, collecting intel for optimization.',
  },
  {
    id: 4,
    name: 'TurboFan JIT',
    zone: 'v8',
    color: '#a78bfa',
    colorName: 'TurboFan Purple',
    time: '~5ms',
    lines: 15000,
    depth: '1,000x',
    code: `// Hot code → Machine code
// Sea-of-nodes IR optimization
// Inlining, constant folding, dead code elimination
// Output: native x86_64 instructions

mov rax, [console_ptr]
call [rax + log_offset]`,
    insight: 'If your code runs often, TurboFan compiles it to raw machine code. Maximum speed.',
  },
  {
    id: 5,
    name: 'Node.js C++ Binding',
    zone: 'runtime',
    color: '#fb923c',
    colorName: 'Node Orange',
    time: '~0.01ms',
    lines: 3000,
    depth: '5,000x',
    code: `// Node.js binding (C++)
void Console::Log(
  const FunctionCallbackInfo<Value>& args
) {
  Isolate* isolate = args.GetIsolate();
  String::Utf8Value str(isolate, args[0]);
  // → Pass to libuv
}`,
    insight: 'console.log isn\'t JavaScript. It\'s a C++ bridge to the operating system.',
  },
  {
    id: 6,
    name: 'libuv',
    zone: 'runtime',
    color: '#f472b6',
    colorName: 'libuv Pink',
    time: '~0.01ms',
    lines: 5000,
    depth: '10,000x',
    code: `// Cross-platform I/O (C)
uv_write_t* req = malloc(sizeof(*req));
uv_buf_t buf = uv_buf_init("Hello", 5);
uv_write(req, stdout_handle, &buf, 1, callback);`,
    insight: 'libuv speaks to every OS in its native tongue. One API, universal translation.',
  },
  {
    id: 7,
    name: 'System Call',
    zone: 'kernel',
    color: '#ef4444',
    colorName: 'Syscall Red',
    time: '~0.1ms',
    lines: 500,
    depth: '50,000x',
    code: `; x86_64 Assembly - The Boundary
mov rax, 1        ; syscall: write
mov rdi, 1        ; fd: stdout
mov rsi, msg      ; buffer pointer
mov rdx, 5        ; byte count
syscall           ; RING 0 TRANSITION`,
    insight: 'The syscall instruction crosses the boundary. User space → Kernel space. Ring 3 → Ring 0.',
    isBarrier: true,
  },
  {
    id: 8,
    name: 'Linux Kernel',
    zone: 'kernel',
    color: '#dc2626',
    colorName: 'Kernel Crimson',
    time: '~0.05ms',
    lines: 8000,
    depth: '100,000x',
    code: `// Linux kernel (fs/read_write.c)
SYSCALL_DEFINE3(write, unsigned int, fd,
                const char __user *, buf,
                size_t, count) {
    struct fd f = fdget_pos(fd);
    ret = vfs_write(f.file, buf, count, ppos);
    return ret;
}`,
    insight: 'The kernel validates everything. File descriptors, permissions, memory addresses. Trust no one.',
  },
  {
    id: 9,
    name: 'TTY Driver',
    zone: 'kernel',
    color: '#b91c1c',
    colorName: 'Driver Maroon',
    time: '~0.01ms',
    lines: 2000,
    depth: '500,000x',
    code: `// TTY subsystem (drivers/tty/tty_io.c)
static ssize_t tty_write(struct file *file,
                         const char __user *buf,
                         size_t count, loff_t *ppos) {
    // Line discipline processing
    // → Hardware buffer
}`,
    insight: 'The terminal driver: line buffering, special characters, the last software stop.',
  },
  {
    id: 10,
    name: 'Hardware Abstraction',
    zone: 'hardware',
    color: '#f97316',
    colorName: 'Hardware Amber',
    time: '~0.001ms',
    lines: 1000,
    depth: '1,000,000x',
    code: `// Memory-mapped I/O
volatile uint32_t* uart = (uint32_t*)0xFE201000;

for (int i = 0; i < 5; i++) {
    while (*uart_status & TX_FULL);  // Spin wait
    *uart_data = "Hello"[i];         // Direct write
}`,
    insight: 'These memory addresses aren\'t RAM. They\'re wires to physical silicon.',
  },
  {
    id: 11,
    name: 'CPU Pipeline',
    zone: 'hardware',
    color: '#eab308',
    colorName: 'Pipeline Gold',
    time: '~1ns/instr',
    lines: 0,
    depth: '10,000,000x',
    code: `╔═══════════╦═══════════╦═══════════╦═══════════╦═══════════╗
║   FETCH   ║  DECODE   ║  EXECUTE  ║  MEMORY   ║ WRITEBACK ║
╠═══════════╬═══════════╬═══════════╬═══════════╬═══════════╣
║ Load next ║   Parse   ║    ALU    ║   Load/   ║   Store   ║
║   instr   ║  opcode   ║  compute  ║   Store   ║  result   ║
╚═══════════╩═══════════╩═══════════╩═══════════╩═══════════╝

// 5 instructions in flight simultaneously
// ~10,000 total cycles for console.log`,
    insight: 'The pipeline: 5 instructions processed simultaneously. Assembly line for electrons.',
  },
  {
    id: 12,
    name: 'Electrons',
    zone: 'physics',
    color: '#22d3ee',
    colorName: 'Electric Cyan',
    time: '~0.1ns',
    lines: 0,
    depth: '∞',
    code: `╔══════════════════════════════════════════════════╗
║               TRANSISTOR GATE                    ║
║                                                  ║
║     Gate ────┐                                   ║
║              ▼                                   ║
║   Source ═══╗ ╔═══ Drain                        ║
║             ╚═╝                                  ║
║           Channel                                ║
║                                                  ║
║   Vth = 0.7V  │  Switch: ~100 picoseconds       ║
║   3nm node    │  Billions per chip              ║
╚══════════════════════════════════════════════════╝`,
    insight: 'At the bottom: voltage. 0.7 volts opens the gate. Electrons flow. That\'s all computation ever was.',
    isEnd: true,
  },
];

// ============================================
// DEPTH HUD COMPONENT
// ============================================
interface DepthHUDProps {
  currentLayer: number;
  totalLayers: number;
  cumulativeLines: number;
  currentZone: string;
}

const DepthHUD: React.FC<DepthHUDProps> = ({ currentLayer, totalLayers, cumulativeLines, currentZone }) => {
  const zoneColors: Record<string, string> = {
    user: '#f7df1e',
    v8: '#22d3ee',
    runtime: '#fb923c',
    kernel: '#ef4444',
    hardware: '#f97316',
    physics: '#22d3ee',
  };

  const zoneLabels: Record<string, string> = {
    user: 'USER SPACE',
    v8: 'V8 ENGINE',
    runtime: 'RUNTIME',
    kernel: 'KERNEL',
    hardware: 'HARDWARE',
    physics: 'PHYSICS',
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      className="fixed left-6 top-1/2 -translate-y-1/2 z-50 hidden lg:block"
    >
      <div className="bg-black/80 backdrop-blur-md border border-white/10 rounded-lg p-4 w-48">
        {/* Zone indicator */}
        <div className="mb-4 pb-4 border-b border-white/10">
          <p className="text-[10px] font-mono text-white/40 mb-1">ZONE</p>
          <p
            className="text-sm font-mono font-bold"
            style={{ color: zoneColors[currentZone] || '#fff' }}
          >
            {zoneLabels[currentZone] || 'UNKNOWN'}
          </p>
        </div>

        {/* Depth gauge */}
        <div className="mb-4">
          <p className="text-[10px] font-mono text-white/40 mb-2">DEPTH</p>
          <div className="flex flex-col gap-1">
            {layers.map((layer, i) => (
              <div key={layer.id} className="flex items-center gap-2">
                <motion.div
                  className="h-2 rounded-sm transition-all duration-300"
                  style={{
                    width: i < currentLayer ? '100%' : '20%',
                    backgroundColor: i < currentLayer ? layer.color : '#1a1a2f',
                    opacity: i < currentLayer ? 1 : 0.3,
                  }}
                  animate={{
                    scale: i === currentLayer - 1 ? [1, 1.1, 1] : 1,
                  }}
                  transition={{ duration: 0.5, repeat: i === currentLayer - 1 ? Infinity : 0 }}
                />
              </div>
            ))}
          </div>
          <p className="text-xs font-mono text-white/60 mt-2 text-center">
            {currentLayer}/{totalLayers}
          </p>
        </div>

        {/* Stats */}
        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-mono text-white/40">LINES ACTIVE</p>
            <p className="text-lg font-mono text-[#22d3ee]">
              {cumulativeLines.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-mono text-white/40">ZOOM LEVEL</p>
            <p className="text-lg font-mono text-[#a78bfa]">
              {layers[Math.min(currentLayer, layers.length - 1)]?.depth || '1x'}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// TYPEWRITER CODE COMPONENT
// ============================================
interface TypewriterCodeProps {
  code: string;
  color: string;
  isVisible: boolean;
}

const TypewriterCode: React.FC<TypewriterCodeProps> = ({ code, color, isVisible }) => {
  const [displayedCode, setDisplayedCode] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setDisplayedCode('');
      setIsComplete(false);
      return;
    }

    let index = 0;
    const interval = setInterval(() => {
      if (index < code.length) {
        setDisplayedCode(code.slice(0, index + 1));
        index++;
      } else {
        setIsComplete(true);
        clearInterval(interval);
      }
    }, 15);

    return () => clearInterval(interval);
  }, [code, isVisible]);

  return (
    <pre
      className="font-mono text-sm leading-relaxed overflow-x-auto"
      style={{ color }}
    >
      <code>
        {displayedCode}
        {!isComplete && <span className="cursor-blink">▋</span>}
      </code>
    </pre>
  );
};

// ============================================
// KERNEL BARRIER COMPONENT
// ============================================
const KernelBarrier: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
  useEffect(() => {
    if (isVisible) {
      soundEngine.playTransition('kernel');
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-red-900/20" />

      {/* Warning lines */}
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-32 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="h-px bg-red-500/50"
            style={{ marginBottom: '16px' }}
            animate={{
              scaleX: [0, 1, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 1,
              delay: i * 0.1,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Warning text */}
      <motion.div
        className="relative z-10 text-center"
        animate={{
          opacity: [0, 1, 1, 0],
        }}
        transition={{ duration: 2, times: [0, 0.2, 0.8, 1] }}
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <AlertTriangle className="w-8 h-8 text-red-500" />
          <span className="font-mono text-red-500 text-2xl font-bold tracking-wider">
            RING 0 BOUNDARY
          </span>
          <AlertTriangle className="w-8 h-8 text-red-500" />
        </div>
        <p className="font-mono text-red-400/80 text-sm">
          ENTERING KERNEL SPACE
        </p>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AbstractionStackStudy = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [currentLayer, setCurrentLayer] = useState(0);
  const [showKernelBarrier, setShowKernelBarrier] = useState(false);
  const [showElectrons, setShowElectrons] = useState(false);
  const lastKernelTrigger = useRef(false);
  const lastElectronTrigger = useRef(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Calculate current layer based on scroll
  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      const layer = Math.floor(latest * 13); // 0-12
      setCurrentLayer(Math.min(layer, 12));

      // Update sound
      soundEngine.updateDepth(layer);

      // Trigger kernel barrier at layer 7
      if (layer >= 7 && !lastKernelTrigger.current) {
        setShowKernelBarrier(true);
        lastKernelTrigger.current = true;
        setTimeout(() => setShowKernelBarrier(false), 2000);
      }
      if (layer < 7) {
        lastKernelTrigger.current = false;
      }

      // Show electrons at layer 12
      if (layer >= 12 && !lastElectronTrigger.current) {
        setShowElectrons(true);
        soundEngine.playTransition('electron');
        lastElectronTrigger.current = true;
      }
      if (layer < 12) {
        setShowElectrons(false);
        lastElectronTrigger.current = false;
      }
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  // Toggle sound
  const toggleSound = useCallback(() => {
    setSoundEnabled(!soundEnabled);
    soundEngine.setEnabled(!soundEnabled);
  }, [soundEnabled]);

  // Calculate cumulative lines
  const cumulativeLines = useMemo(() => {
    return layers.slice(0, currentLayer).reduce((sum, l) => sum + l.lines, 0);
  }, [currentLayer]);

  // Get current zone
  const currentZone = layers[Math.max(0, currentLayer - 1)]?.zone || 'user';

  // Zoom scale based on scroll
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.5]);
  const opacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.8]);

  return (
    <div ref={containerRef} className="bg-[#05050f] min-h-[1400vh] font-display relative">
      <FontLoader />

      {/* Electron particle system */}
      <ElectronParticles active={showElectrons} />

      {/* Kernel barrier overlay */}
      <AnimatePresence>
        <KernelBarrier isVisible={showKernelBarrier} />
      </AnimatePresence>

      {/* Fixed HUD */}
      <DepthHUD
        currentLayer={currentLayer}
        totalLayers={12}
        cumulativeLines={cumulativeLines}
        currentZone={currentZone}
      />

      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-[#05050f] via-[#05050f]/80 to-transparent">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/labs')}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-4"
        >
          <span className="text-white/40 text-sm font-mono hidden sm:block">
            Lab #08 • The Abstraction Stack
          </span>
          <button
            onClick={toggleSound}
            className={`p-2 rounded-full transition-all ${
              soundEnabled
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                : 'bg-white/5 text-white/40 border border-white/10'
            }`}
            title={soundEnabled ? 'Sound on' : 'Sound off'}
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </motion.div>
      </div>

      {/* HERO SECTION */}
      <motion.section
        className="h-screen flex flex-col items-center justify-center px-6 relative z-10 bg-[#05050f]"
        style={{ opacity }}
      >
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-radial from-cyan-900/10 via-transparent to-transparent" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center max-w-5xl relative z-10"
        >
          <motion.p
            className="font-mono text-cyan-400 text-sm tracking-[0.3em] mb-8"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            THOUGHT EXPERIMENT
          </motion.p>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-[0.9]">
            How far is
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-cyan-400 to-purple-400">
              your code
            </span>
            <br />
            from electrons?
          </h1>

          {/* The console.log - THE thing we're tracing */}
          <motion.div
            className="inline-block relative"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="bg-[#0a0a1a] border-2 border-yellow-400/30 rounded-xl px-8 py-5 font-mono text-2xl md:text-3xl crt-glow">
              <span className="text-yellow-400">console</span>
              <span className="text-white/80">.</span>
              <span className="text-cyan-400">log</span>
              <span className="text-white/80">(</span>
              <span className="text-orange-300">"Hello"</span>
              <span className="text-white/80">)</span>
              <span className="text-white/40">;</span>
            </div>

            {/* Glow effect */}
            <div className="absolute -inset-4 bg-yellow-400/10 rounded-2xl blur-xl -z-10" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white/50 text-lg md:text-xl mt-12 max-w-2xl mx-auto"
          >
            27 characters. 12 layers. 50,000 lines of code.
            <br />
            <span className="text-cyan-400">6 milliseconds to electrons.</span>
          </motion.p>

          {/* Scroll indicator */}
          <motion.div
            className="mt-20"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <ChevronDown className="w-8 h-8 text-white/30 mx-auto" />
            <p className="text-[10px] font-mono text-white/30 mt-2 tracking-widest">
              SCROLL TO ZOOM DEEPER
            </p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* LAYER SECTIONS */}
      {layers.map((layer, index) => (
        <section
          key={layer.id}
          className="min-h-screen flex items-center px-6 md:px-12 lg:px-24 py-32 relative"
          style={{
            background: `linear-gradient(to bottom, ${layer.color}05, transparent)`,
          }}
        >
          {/* Giant layer number in background */}
          <motion.div
            className="absolute right-4 md:right-16 top-1/2 -translate-y-1/2 text-[12rem] md:text-[20rem] font-black select-none pointer-events-none"
            style={{
              color: layer.color,
              opacity: 0.03,
            }}
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 0.03, x: 0 }}
            viewport={{ once: true }}
          >
            {String(layer.id).padStart(2, '0')}
          </motion.div>

          {/* Zone indicator line */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1"
            style={{ backgroundColor: layer.color, opacity: 0.5 }}
          />

          <motion.div
            initial={{ opacity: 0, x: -50, scale: 0.95 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, margin: "-20%" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl relative z-10 w-full"
          >
            {/* Layer header */}
            <div className="flex items-center gap-4 mb-6">
              <div
                className="w-3 h-3 rounded-full pulse-glow"
                style={{ backgroundColor: layer.color }}
              />
              <span
                className="font-mono text-sm tracking-wider"
                style={{ color: layer.color }}
              >
                LAYER {layer.id} OF 12
              </span>
              <span className="font-mono text-xs text-white/30">
                {layer.time}
              </span>
            </div>

            {/* Layer name */}
            <h2
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6"
              style={{ color: layer.color }}
            >
              {layer.name}
            </h2>

            {/* Insight */}
            <p className="text-xl md:text-2xl text-white/70 mb-10 max-w-2xl leading-relaxed">
              {layer.insight}
            </p>

            {/* Code block */}
            <div
              className="bg-[#0a0a15] border rounded-xl overflow-hidden"
              style={{ borderColor: `${layer.color}30` }}
            >
              <div
                className="px-4 py-2 border-b flex items-center justify-between"
                style={{
                  borderColor: `${layer.color}20`,
                  backgroundColor: `${layer.color}08`
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/50" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                  <div className="w-3 h-3 rounded-full bg-green-500/50" />
                </div>
                <span
                  className="text-xs font-mono"
                  style={{ color: `${layer.color}80` }}
                >
                  {layer.zone} • zoom: {layer.depth}
                </span>
              </div>
              <div className="p-6">
                <TypewriterCode
                  code={layer.code}
                  color={layer.color}
                  isVisible={currentLayer >= layer.id}
                />
              </div>
            </div>

            {/* Stats row */}
            {layer.lines > 0 && (
              <div className="mt-6 flex gap-8">
                <div>
                  <p className="text-xs font-mono text-white/30 mb-1">LINES OF CODE</p>
                  <p
                    className="text-2xl font-mono font-bold"
                    style={{ color: layer.color }}
                  >
                    {layer.lines.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-mono text-white/30 mb-1">CUMULATIVE</p>
                  <p className="text-2xl font-mono font-bold text-white/60">
                    {layers.slice(0, index + 1).reduce((s, l) => s + l.lines, 0).toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            {/* Barrier indicator */}
            {layer.isBarrier && (
              <motion.div
                className="mt-8 p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center gap-3"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="font-mono text-red-400 text-sm">
                  CROSSING RING 0 BOUNDARY • USER SPACE → KERNEL SPACE
                </span>
              </motion.div>
            )}

            {/* End indicator */}
            {layer.isEnd && (
              <motion.div
                className="mt-8 p-6 bg-cyan-900/20 border border-cyan-500/30 rounded-xl"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Zap className="w-6 h-6 text-cyan-400" />
                  <span className="font-mono text-cyan-400 font-bold">
                    THE END OF ABSTRACTION
                  </span>
                </div>
                <p className="text-cyan-200/70">
                  Below this, there is no code. Only physics.
                  Voltage differences in silicon, switching at billions of times per second.
                  <br /><br />
                  <span className="text-cyan-300">
                    Your "Hello" is, at its core, just electrons flowing through sand.
                  </span>
                </p>
              </motion.div>
            )}
          </motion.div>
        </section>
      ))}

      {/* REVELATION SECTION */}
      <section className="min-h-screen flex items-center justify-center px-6 py-32 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-px bg-gradient-to-b from-transparent via-cyan-500/20 to-transparent"
              style={{
                left: `${(i / 20) * 100}%`,
                height: '200%',
                top: '-50%',
              }}
              animate={{
                y: ['0%', '50%'],
              }}
              transition={{
                duration: 10 + i * 0.5,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center max-w-5xl relative z-10"
        >
          <motion.p
            className="font-mono text-cyan-400 text-sm tracking-[0.3em] mb-8"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            THE REVELATION
          </motion.p>

          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-12">
            Your 1 line activated:
          </h2>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
            {[
              { value: '~50,000', label: 'Lines of Code', color: '#f7df1e' },
              { value: '2', label: 'Context Switches', color: '#a78bfa' },
              { value: '~10,000', label: 'CPU Cycles', color: '#fb923c' },
              { value: '~6ms', label: 'Total Time', color: '#22d3ee' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6"
              >
                <p
                  className="text-3xl md:text-4xl font-mono font-bold mb-2"
                  style={{ color: stat.color }}
                >
                  {stat.value}
                </p>
                <p className="text-sm text-white/50">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* The insight */}
          <motion.div
            className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              "Simple" is a beautiful lie. Every abstraction is a gift from engineers who came before.
            </p>
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-lg text-cyan-300">
                The next time you write{' '}
                <code className="bg-cyan-900/30 px-2 py-1 rounded text-yellow-400">console.log</code>,
                remember the 50,000 lines working to make it feel instant.
              </p>
            </div>
          </motion.div>

          {/* Layer journey */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {layers.map((layer) => (
              <motion.span
                key={layer.id}
                className="px-3 py-1 rounded-full text-xs font-mono"
                style={{
                  backgroundColor: `${layer.color}15`,
                  color: layer.color,
                  borderWidth: 1,
                  borderColor: `${layer.color}30`,
                }}
                whileHover={{ scale: 1.1 }}
              >
                {layer.name}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 border-t border-white/10 relative">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-cyan-400 text-sm mb-6">// now you know</p>

            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              The Stack Never Sleeps
            </h2>

            <p className="text-white/50 mb-10 max-w-xl mx-auto text-lg">
              Every keystroke. Every click. Every API call.
              <br />
              Twelve layers deep, electrons dance.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/labs')}
                className="px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-xl transition-colors"
              >
                Explore More Labs
              </motion.button>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="https://v8.dev/blog"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-white/20 hover:border-white/40 text-white font-medium rounded-xl transition-colors"
              >
                Read V8 Blog →
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <p>
            Compiled by{' '}
            <a href="https://twitter.com/AKJosh21" className="text-cyan-400 hover:underline">
              @josharsh
            </a>
          </p>
          <p className="font-mono text-xs">
            Sources: V8 Blog, Linux Kernel, libuv, Intel Manuals
          </p>
        </div>
      </footer>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="abstraction-stack" variant="dark" />
    </div>
  );
};

export default AbstractionStackStudy;
